/**
 * Tenant Branding Helper (frontend)
 *
 * Reads firm branding (firmName, attorneyName, firmAddress, firmPhone, firmEmail)
 * from the active tenant profile at `accounts/{accountId}/onboarding/profile`.
 *
 * This is the single source of truth for firm branding in client-side
 * document generation (PDF/DOCX titles, footers, copyright strings) and
 * any prompt/chat persona strings that previously hardcoded
 * "The Law Offices of Fernando Hidalgo, Inc.".
 *
 * Fallback policy:
 * - No tenant context (accountId is null/undefined)                 -> Fernando defaults
 * - accountId === 'fernando-hidalgo'                                -> Fernando defaults
 * - Any other accountId, profile missing firmName / profile absent  -> NEUTRAL defaults
 *   (we intentionally do NOT leak Fernando branding into T1/T2 output)
 *
 * MT-QA-BUG-001: fix hardcoded Fernando branding leaking into T1/T2 output.
 */

export const FERNANDO_ACCOUNT_ID = 'fernando-hidalgo';

/**
 * Known Fernando attorney email addresses. Any signed-in account whose
 * onboarding profile attorneyEmail/contactEmail matches one of these is
 * treated as the Fernando tenant for branding purposes. Fixes the QA
 * regression (2026-05-08) where the real Firebase UID did not match the
 * literal 'fernando-hidalgo' accountId and NEUTRAL_BRANDING ("Your Law
 * Firm") leaked into Fernando-account PDFs.
 */
export const FERNANDO_ATTORNEY_EMAILS: ReadonlyArray<string> = Object.freeze([
  'fernandohidalgoesq@yahoo.com',
]);

/**
 * Product/generator name rendered on every PDF/DOCX as
 * "Generated by: {{PRODUCT_NAME}}". This is the user-facing product label
 * for the white-label deep-research product; it is NOT tenant-specific and
 * MUST NOT be overridden per tenant.
 */
export const PRODUCT_NAME = 'Prest0n';

/** Canonical Fernando firm branding — used for widget / justiceisurright / public chat path. */
export const FERNANDO_BRANDING: Readonly<TenantBranding> = Object.freeze({
  firmName: 'The Law Offices of Fernando Hidalgo, Inc.',
  attorneyName: 'Fernando Hidalgo',
  firmAddress: null,
  firmPhone: null,
  firmEmail: 'fernandohidalgoesq@yahoo.com',
});

/**
 * Neutral, non-Fernando defaults used when a non-Fernando tenant profile is
 * missing or incomplete. Prevents Fernando branding from leaking into T1/T2
 * output while keeping downstream string formatting safe.
 */
export const NEUTRAL_BRANDING: Readonly<TenantBranding> = Object.freeze({
  firmName: 'Your Law Firm',
  attorneyName: null,
  firmAddress: null,
  firmPhone: null,
  firmEmail: null,
});

export interface TenantBranding {
  firmName: string;
  attorneyName: string | null;
  firmAddress: string | null;
  firmPhone: string | null;
  firmEmail: string | null;
}

// In-memory cache keyed by accountId (session-scoped).
const brandingCache = new Map<string, TenantBranding>();

/**
 * Clear the branding cache. Useful when switching tenants (e.g., account
 * switcher) so that stale branding does not leak across sessions.
 */
export function clearTenantBrandingCache(): void {
  brandingCache.clear();
}

/**
 * Synchronous check — returns true if this accountId should be treated as
 * the Fernando-facing path (widget owner). Safe to call without Firebase.
 */
export function isFernandoAccount(accountId?: string | null): boolean {
  return accountId === FERNANDO_ACCOUNT_ID;
}

/**
 * Returns true if the given profile attorneyEmail / contactEmail matches a
 * known Fernando attorney email. Used as a fallback when the accountId is
 * a real Firebase UID (not the literal 'fernando-hidalgo').
 */
export function isFernandoProfile(profile: Record<string, unknown> | null | undefined): boolean {
  if (!profile) return false;
  const candidate = (
    coerceString((profile as any).attorneyEmail) ||
    coerceString((profile as any).contactEmail) ||
    coerceString((profile as any).firmEmail) ||
    ''
  ).toLowerCase();
  if (!candidate) return false;
  return FERNANDO_ATTORNEY_EMAILS.some((e) => e.toLowerCase() === candidate);
}

/**
 * Fetch the tenant branding for the given accountId.
 *
 * - `null` / `undefined` accountId       -> Fernando defaults (widget / pre-auth)
 * - `fernando-hidalgo` accountId         -> Fernando defaults
 * - anything else                         -> read `accounts/{accountId}/onboarding/profile`
 *                                            and use `firmName` / optional branding fields.
 *                                            Falls back to NEUTRAL_BRANDING if the profile
 *                                            is missing or has no firmName.
 */
export async function fetchTenantBranding(
  accountId?: string | null,
): Promise<TenantBranding> {
  if (!accountId || accountId === FERNANDO_ACCOUNT_ID) {
    return { ...FERNANDO_BRANDING };
  }

  const cached = brandingCache.get(accountId);
  if (cached) return cached;

  try {
    // Dynamic imports to avoid pulling firebase into non-tenant contexts.
    const { getFirestore, doc, getDoc } = await import('firebase/firestore');
    const { getApp } = await import('firebase/app');
    const db = getFirestore(getApp());

    // MT-QA-BUG-001/010 (2026-05-08 G1/G3 regression): firm branding may
    // live at any of several Firestore paths depending on how the account
    // was provisioned (trial self-onboarded vs. server-side provisioned vs.
    // VM-memory-populated). Previously we only read
    // `accounts/{uid}/onboarding/profile`, which caused T1/T2 translation
    // PDFs and Deep Research PDFs to emit "Your Law Firm" whenever that
    // doc was missing or didn't carry `firmName`.
    //
    // Resolution order (first path with a non-empty `firmName` wins):
    //   1. accounts/{accountId}/onboarding/profile     — self-serve trial onboarding
    //   2. accounts/{accountId}/profile/main           — server-provisioned canonical profile
    //   3. accounts/{accountId}/memory/onboarding_profile — VM memory doc (fallback)
    //
    // On every read we also check `isFernandoProfile` so Fernando's own
    // account stays Fernando-branded even when the canonical profile
    // lacks a literal accountId match.
    const candidatePaths: Array<[string, string, string, string]> = [
      ['accounts', accountId, 'onboarding', 'profile'],
      ['accounts', accountId, 'profile', 'main'],
      ['accounts', accountId, 'memory', 'onboarding_profile'],
    ];

    let resolvedData: Record<string, unknown> | null = null;
    let resolvedPath: string | null = null;

    for (const [c1, d1, c2, d2] of candidatePaths) {
      try {
        const snap = await getDoc(doc(db, c1, d1, c2, d2));
        if (!snap.exists()) continue;
        const data = snap.data() as Record<string, unknown>;
        const firmNameCandidate = coerceString(data.firmName) || coerceString(data.displayName);
        if (firmNameCandidate) {
          resolvedData = data;
          resolvedPath = `${c1}/${d1}/${c2}/${d2}`;
          break;
        }
        // Remember the first existing doc even if firmName is empty, so we
        // at least get Fernando-email detection when that's the only profile.
        if (!resolvedData) {
          resolvedData = data;
          resolvedPath = `${c1}/${d1}/${c2}/${d2}`;
        }
      } catch (innerErr) {
        // Permission denied on one path is not fatal — try the next.
        console.warn(
          `[tenantBranding] Profile read failed at ${c1}/${d1}/${c2}/${d2}:`,
          innerErr,
        );
      }
    }

    if (!resolvedData) {
      const fallback = { ...NEUTRAL_BRANDING };
      brandingCache.set(accountId, fallback);
      return fallback;
    }

    if (resolvedPath) {
      console.log(`[tenantBranding] Resolved firm branding from ${resolvedPath}`);
    }

    // MT-QA-BUG-001 follow-up: treat any account whose profile belongs to
    // Fernando (by attorney/contact email) as the Fernando tenant even when
    // the accountId is a real Firebase UID rather than 'fernando-hidalgo'.
    if (isFernandoProfile(resolvedData)) {
      const fernando = { ...FERNANDO_BRANDING };
      brandingCache.set(accountId, fernando);
      return fernando;
    }

    const firmName =
      coerceString(resolvedData.firmName) ||
      coerceString(resolvedData.displayName) ||
      NEUTRAL_BRANDING.firmName;
    const attorneyName =
      coerceString(resolvedData.attorneyName) ||
      coerceString(resolvedData.ownerName) ||
      null;
    const firmAddress = coerceString(resolvedData.firmAddress) || null;
    const firmPhone = coerceString(resolvedData.firmPhone) || null;
    const firmEmail =
      coerceString(resolvedData.firmEmail) ||
      coerceString(resolvedData.contactEmail) ||
      null;

    const branding: TenantBranding = {
      firmName,
      attorneyName,
      firmAddress,
      firmPhone,
      firmEmail,
    };
    brandingCache.set(accountId, branding);
    return branding;
  } catch (err) {
    console.warn('[tenantBranding] Failed to load tenant profile — falling back to neutral defaults:', err);
    return { ...NEUTRAL_BRANDING };
  }
}

/**
 * Returns true if the currently signed-in Firebase user's email matches a
 * known Fernando attorney email. This is the most-robust Fernando-identity
 * short-circuit for the account `/desktop` path — the profile-based check
 * in `fetchTenantBranding` only works when the profile doc actually has
 * `attorneyEmail` / `contactEmail` / `firmEmail` set, which is not the
 * case for the real production Fernando profile. Checking
 * `auth.currentUser.email` bypasses the profile shape entirely.
 *
 * MT-QA-BUG-001 follow-up (2026-05-08 §132a desktop regression).
 */
export async function isFernandoByAuthEmail(): Promise<boolean> {
  try {
    const { getAuth } = await import('firebase/auth');
    const { getApp } = await import('firebase/app');
    const auth = getAuth(getApp());
    const email = (auth.currentUser?.email || '').toLowerCase().trim();
    if (!email) return false;
    return FERNANDO_ATTORNEY_EMAILS.some((e) => e.toLowerCase() === email);
  } catch {
    return false;
  }
}

/**
 * Convenience helper that resolves the current signed-in user's accountId
 * (via prest0nRuntime) and fetches branding. If the user is not yet signed
 * in, returns Fernando defaults (widget / public chat path).
 *
 * MT-QA-BUG-001 follow-up (2026-05-08): we now short-circuit to Fernando
 * branding when the signed-in user's email is a known Fernando attorney
 * email, BEFORE attempting the profile lookup. The profile document on
 * the real Fernando account does not carry the attorneyEmail field that
 * `isFernandoProfile` looks for, so without this email short-circuit the
 * helper falls through to NEUTRAL_BRANDING ("Your Law Firm") and that
 * string ends up on the Fernando-account `/desktop` deep-research PDFs.
 */
export async function fetchCurrentTenantBranding(): Promise<TenantBranding> {
  try {
    // Option 3 short-circuit: trust the signed-in user's verified email
    // before we even ask Firestore for a profile.
    if (await isFernandoByAuthEmail()) {
      return { ...FERNANDO_BRANDING };
    }
    const { resolveAccountIdFromAuth } = await import('./prest0nRuntime');
    const accountId = resolveAccountIdFromAuth();
    return fetchTenantBranding(accountId);
  } catch {
    return { ...FERNANDO_BRANDING };
  }
}

function coerceString(v: unknown): string {
  if (typeof v !== 'string') return '';
  return v.trim();
}
